import React, { useRef, useState } from "react";
import usePayrollStore from "../../../config/stores/administration/usePayrollStore";
import { isEmpty } from "lodash";
import { Alert, Card, Col, message, Popconfirm, Row, Space, Tag } from "antd";
import AppLoader from "../../../components/common/AppLoader";
import dayjs from "dayjs";
import AppButton from "../../../components/common/AppButton";
import {
  AiFillCloseCircle,
  AiOutlinePrinter,
  AiOutlineReload,
} from "react-icons/ai";
import { CheckOutlined, CloseOutlined, UndoOutlined, FileSearchOutlined } from "@ant-design/icons";
import appConfig from "../../../config/config.json";
import AntdTable from "../../../components/common/AntdTable";
import { inputNumberFormatter } from "../../../config/constants";
import { useReactToPrint } from "react-to-print";
import PrintOutHeader from "../../../components/shared/PrintOutHeader";

const PayrollDetails = () => {
  const payrollStore = usePayrollStore();
  const [showHeader, setShowHeader] = useState(false);

  const { payrollDetails, gettingPayrollDetails, showPayrollDetails, downloadingPayrollPdf } = payrollStore;
  const month = payrollDetails?.month 
    ? dayjs().month(payrollDetails.month - 1).format("MMMM")
    : "";

  const firstDay = payrollDetails?.month
    ? dayjs().month(payrollDetails.month - 1).startOf("month").format("DD")
    : "";
  const lastDay = payrollDetails?.month
    ? dayjs().month(payrollDetails.month - 1).endOf("month").format("DD")
    : "";

  const closePayroll = () => {
    payrollStore.setShowPayrollDetails(false, null);
  };

  const getStatusColor = (status) => {
    if (!status) return "default";
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case appConfig.status.approved?.toLowerCase():
        return "green";
      case appConfig.status.checked?.toLowerCase():
        return "blue";
      case appConfig.status.rejected?.toLowerCase():
        return "red";
      case appConfig.status.pending?.toLowerCase():
        return "orange";
      default:
        return "default";
    }
  };

  const approvedAt = dayjs(payrollDetails?.approved_at);

  const componentRef = useRef();

  const pageStyle = `
  @page {size: A4 landscape);
   @media all {
     .pagebreak {
       page-break-before: always;
     }
   }
   @media print {
     html, body {
       height: initial;
       overflow: initial;
       font-size: 12px !important;
     }
     @page {size: A4 landscape); 
       .pagebreak {
       page-break-before: always;
     }
     @page {size: A4 portrait;max-height:100%; max-width:100%}
   }
   .image {
     height: 48px !important,
   }
 `;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    contentRef: componentRef,
    pageStyle,
    onAfterPrint: () => {
      setShowHeader(false);
    },
  });

  const onPrint = () => {
    setShowHeader(true);
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  const onDownloadPdf = async () => {
    if (!payrollDetails?.id) {
      message.error("Payroll ID is missing");
      return;
    }
    try {
      await payrollStore.downloadPayrollPdf(payrollDetails.id);
      message.success("Payroll PDF downloaded successfully");
    } catch (error) {
      message.error("Failed to download payroll PDF");
    }
  };

  const payrollBreakdownData = [];
  
  const projectColumns = [];
  const projectDataMap = {};

  if (payrollDetails) {
    if (payrollDetails.projectAllocations) {
      let allocations = [];
      if (Array.isArray(payrollDetails.projectAllocations)) {
        allocations = payrollDetails.projectAllocations;
      } else if (typeof payrollDetails.projectAllocations === 'object') {
        Object.keys(payrollDetails.projectAllocations).forEach(projectId => {
          const alloc = payrollDetails.projectAllocations[projectId];
          if (alloc) {
            allocations.push({
              project_id: projectId,
              project: { name: typeof alloc === 'object' ? alloc.name : projectId },
              amount: typeof alloc === 'object' ? alloc.amount : alloc,
            });
          }
        });
      }
      
      allocations.forEach((alloc) => {
        if (alloc && (alloc.amount > 0 || alloc.amount !== undefined)) {
          const projectId = alloc.project_id || alloc.project?.id || 'unknown';
          const projectName = alloc.project?.name || "N/A";
          
          if (!projectColumns.find(p => p.key === projectId)) {
            projectColumns.push({
              key: projectId,
              title: projectName.toUpperCase(),
              dataIndex: `project_${projectId}`,
              width: 150,
              align: "right",
            });
          }
          
          projectDataMap[`project_${projectId}`] = alloc.amount || 0;
        }
      });
    }

    const breakdownRow = {
      key: "breakdown",
      item: "",
      ...projectDataMap,
    };
    if (payrollDetails.transport_allowance > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "transport",
        item: "Transport Allowance",
        amount: `UGX ${inputNumberFormatter(payrollDetails.transport_allowance)}`,
      });
    }
    if (payrollDetails.housing_allowance > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "housing",
        item: "Housing Allowance",
        amount: `UGX ${inputNumberFormatter(payrollDetails.housing_allowance)}`,
      });
    }
    if (payrollDetails.medical_allowances > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "medical",
        item: "Medical Allowances",
        amount: `UGX ${inputNumberFormatter(payrollDetails.medical_allowances)}`,
      });
    }
    if (payrollDetails.other_allowances > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "other",
        item: "Other Allowances",
        amount: `UGX ${inputNumberFormatter(payrollDetails.other_allowances)}`,
      });
    }

    payrollBreakdownData.push({
      ...breakdownRow,
      key: "gross_pay",
      item: "Gross Pay",
      amount: `UGX ${inputNumberFormatter(payrollDetails.gross_pay || 0)}`,
    });
    payrollBreakdownData.push({
      ...breakdownRow,
      key: "total_earnings",
      item: "Total Earnings",
      amount: `UGX ${inputNumberFormatter(payrollDetails.total_earnings || 0)}`,
    });

    if (payrollDetails.paye > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "paye",
        item: "PAYE",
        amount: `UGX ${inputNumberFormatter(payrollDetails.paye)}`,
      });
    }
    if (payrollDetails.nssf_deduction > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "nssf_employee",
        item: "NSSF Deduction (5%)",
        amount: `UGX ${inputNumberFormatter(payrollDetails.nssf_deduction)}`,
      });
    }
    if (payrollDetails.local_service_tax > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "local_service_tax",
        item: "Local Service Tax",
        amount: `UGX ${inputNumberFormatter(payrollDetails.local_service_tax)}`,
      });
    }
    if (payrollDetails.salary_advance > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "salary_advance",
        item: "Salary Advance",
        amount: `UGX ${inputNumberFormatter(payrollDetails.salary_advance)}`,
      });
    }
    if (payrollDetails.staff_savings_scheme > 0) {
      payrollBreakdownData.push({
        ...breakdownRow,
        key: "staff_savings",
        item: "Staff Savings Scheme",
        amount: `UGX ${inputNumberFormatter(payrollDetails.staff_savings_scheme)}`,
      });
    }
    payrollBreakdownData.push({
      ...breakdownRow,
      key: "total_deductions",
      item: "Total Deductions",
      amount: `UGX ${inputNumberFormatter(payrollDetails.total_deductions || 0)}`,
    });

    payrollBreakdownData.push({
      ...breakdownRow,
      key: "net_salary",
      item: "Net Salary",
      amount: `UGX ${inputNumberFormatter(payrollDetails.net_salary || 0)}`,
    });
  }

  const payrollBreakdownColumns = [
    {
      title: <span className="fw-bold">ITEM</span>,
      dataIndex: "item",
      key: "item",
      width: "40%",
      render: (text) => <span className="fw-bold">{text}</span>,
    },
    ...projectColumns.map(col => ({
      ...col,
      title: <span className="fw-bold">{col.title}</span>,
      render: (_, record) => {
        const amount = record[col.dataIndex] || 0;
        return amount > 0 ? (
          <span className="fw-bold">UGX {inputNumberFormatter(amount)}</span>
        ) : "-";
      },
    })),
    {
      title: <span className="fw-bold">AMOUNT</span>,
      dataIndex: "amount",
      key: "amount",
      width: "30%",
      align: "right",
      render: (text) => <span className="fw-bold">{text}</span>,
    },
  ];

  if (!showPayrollDetails) {
    return null;
  }

  return (
    <div>
      {!isEmpty(payrollStore.getPayrollDetailsError) && (
        <Alert
          message={payrollStore.getPayrollDetailsError?.message}
          type="error"
          showIcon
          className="my-2"
          action={
            <Space>
              <AppButton
                text="Close"
                type="default"
                iconBefore={<AiFillCloseCircle />}
                danger
                onClick={closePayroll}
              />
            </Space>
          }
        />
      )}
      {isEmpty(payrollDetails) && !gettingPayrollDetails && isEmpty(payrollStore.getPayrollDetailsError) ? (
        <AppLoader message="GETTING PAYROLL DETAILS" />
      ) : (
        <Row>
          <Col span={24}>
            <Card
              title={`${month} ${payrollDetails?.year || ""} PAYROLL`.toUpperCase()}
              extra={
                <Space>
                  {/* Check button - only if pending */}
                  {payrollDetails?.status?.toLowerCase() === appConfig.status.pending?.toLowerCase() && (
                    <Popconfirm
                      okText="Yes"
                      cancelText="No"
                      cancelButtonProps={{
                        danger: true,
                      }}
                      okButtonProps={{
                        color: "green",
                      }}
                      onConfirm={async () => {
                        await payrollStore.checkMonthlyPayroll(payrollDetails.id, payrollDetails.month, payrollDetails.year);
                        payrollStore.getAllPayrolls();
                      }}
                      title="Are you sure you want to check this payroll"
                    >
                      <AppButton
                        text="Check"
                        type="default"
                        loadingText="Checking..."
                        iconBefore={<FileSearchOutlined />}
                        loading={payrollStore.checkingPayslip}
                      />
                    </Popconfirm>
                  )}

                  {/* Approve button - only if checked */}
                  {payrollDetails?.status?.toLowerCase() === appConfig.status.checked?.toLowerCase() && (
                    <Popconfirm
                      okText="Yes"
                      cancelText="No"
                      cancelButtonProps={{
                        danger: true,
                      }}
                      okButtonProps={{
                        color: "green",
                      }}
                      onConfirm={async () => {
                        await payrollStore.approveMonthlyPayroll(payrollDetails.id, payrollDetails.month, payrollDetails.year);
                        payrollStore.getAllPayrolls();
                      }}
                      title="Are you sure you want to approve this payroll"
                    >
                      <AppButton
                        text="Approve"
                        type="primary"
                        loadingText="Approving..."
                        iconBefore={<CheckOutlined />}
                        loading={payrollStore.approvingPayslip}
                      />
                    </Popconfirm>
                  )}

                  {/* Reject button - only if pending or checked */}
                  {(payrollDetails?.status?.toLowerCase() === appConfig.status.pending?.toLowerCase() ||
                    payrollDetails?.status?.toLowerCase() === appConfig.status.checked?.toLowerCase()) && (
                    <Popconfirm
                      okText="Yes"
                      cancelText="No"
                      cancelButtonProps={{
                        danger: true,
                      }}
                      okButtonProps={{
                        color: "green",
                      }}
                      onConfirm={async () => {
                        await payrollStore.rejectMonthlyPayroll(payrollDetails.id, payrollDetails.month, payrollDetails.year);
                        payrollStore.getAllPayrolls();
                      }}
                      title="Are you sure you want to reject this payroll"
                    >
                      <AppButton
                        text="Reject"
                        danger
                        loadingText="Rejecting..."
                        iconBefore={<CloseOutlined />}
                        loading={payrollStore.rejectingPayslip}
                      />
                    </Popconfirm>
                  )}

                  {/* Undo Approve button - only if approved */}
                  {payrollDetails?.status?.toLowerCase() === appConfig.status.approved?.toLowerCase() && (
                    <Popconfirm
                      okText="Yes"
                      cancelText="No"
                      cancelButtonProps={{
                        danger: true,
                      }}
                      okButtonProps={{
                        color: "green",
                      }}
                      onConfirm={async () => {
                        await payrollStore.undoApproveMonthlyPayroll(payrollDetails.id, payrollDetails.month, payrollDetails.year);
                        payrollStore.getAllPayrolls();
                      }}
                      title="Are you sure you want to undo the approval of this payroll"
                    >
                      <AppButton
                        text="Undo Approve"
                        type="default"
                        loadingText="Undoing..."
                        iconBefore={<UndoOutlined />}
                        loading={payrollStore.undoingApprovePayslip}
                      />
                    </Popconfirm>
                  )}

                  <AppButton
                    iconBefore={<AiOutlinePrinter />}
                    text="Download"
                    loading={downloadingPayrollPdf}
                    loadingText="Downloading..."
                    onClick={onDownloadPdf}
                  />
                  <AppButton
                    text="Reload"
                    type="default"
                    iconBefore={<AiOutlineReload />}
                    onClick={() =>
                      payrollStore.getPayrollDetails(payrollDetails.id)
                    }
                  />
                  <AppButton
                    text="Close"
                    type="default"
                    iconBefore={<AiFillCloseCircle />}
                    danger
                    onClick={closePayroll}
                  />
                </Space>
              }
            >
              <div className="d-none">
                <div ref={componentRef}>
                  <PrintOutHeader />
                  <table className="table table-bordered table-striped table-sm">
                    <tbody>
                      <tr>
                        <td>PERIOD</td>
                        <td>
                          {firstDay}-{lastDay} {month} {payrollDetails?.year}
                        </td>
                      </tr>
                      <tr>
                        <td>NAME OF STAFF</td>
                        <td>
                          {payrollDetails?.user?.first_name || ""}{" "}
                          {payrollDetails?.user?.other_names || ""}
                        </td>
                      </tr>
                      <tr>
                        <td>POSITION</td>
                        <td>{payrollDetails?.staff_contract?.position || payrollDetails?.user?.role?.role || ""}</td>
                      </tr>
                    <tr>
                      <td>STATUS</td>
                      <td>
                        {payrollDetails?.status ? (
                          <Tag color={getStatusColor(payrollDetails.status)}>
                            {payrollDetails.status.toUpperCase()}
                          </Tag>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                      <tr>
                        <td>APPROVED AT</td>
                        <td>
                          {approvedAt.isValid()
                            ? approvedAt.format("DD/MM/YYYY HH:mm")
                            : "NOT APPROVED"}
                        </td>
                      </tr>
                      <tr>
                        <td>APPROVED BY</td>
                        <td>
                          {`${
                            payrollDetails?.approved_by_user?.first_name || "NOT"
                          } ${
                            payrollDetails?.approved_by_user?.other_names ||
                            " APPROVED"
                          }`}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Payroll Breakdown for Print */}
                  <h6 className="fw-bold mb-2 mt-3">PAYROLL BREAKDOWN</h6>
                  <AntdTable
                    columns={payrollBreakdownColumns}
                    data={payrollBreakdownData}
                    pagination={false}
                    showHeader={true}
                    bordered={true}
                    size="small"
                  />
                </div>
              </div>
              <div>
                <table className="table table-bordered table-striped table-sm">
                  <tbody>
                    <tr>
                      <td>PERIOD</td>
                      <td>
                        {firstDay}-{lastDay} {month} {payrollDetails?.year}
                      </td>
                    </tr>
                    <tr>
                      <td>NAME OF STAFF</td>
                      <td>
                        {payrollDetails?.user?.first_name || ""}{" "}
                        {payrollDetails?.user?.other_names || ""}
                      </td>
                    </tr>
                    <tr>
                      <td>POSITION</td>
                      <td>{payrollDetails?.staff_contract?.position || payrollDetails?.user?.role?.role || ""}</td>
                    </tr>
                    <tr>
                      <td>STATUS</td>
                      <td>
                        {payrollDetails?.status ? (
                          <Tag color={getStatusColor(payrollDetails.status)}>
                            {payrollDetails.status.toUpperCase()}
                          </Tag>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>APPROVED AT</td>
                      <td>
                        {approvedAt.isValid()
                          ? approvedAt.format("DD/MM/YYYY HH:mm")
                          : "NOT APPROVED"}
                      </td>
                    </tr>
                    <tr>
                      <td>APPROVED BY</td>
                      <td>
                        {`${
                          payrollDetails?.approved_by_user?.first_name || "NOT"
                        } ${
                          payrollDetails?.approved_by_user?.other_names ||
                          " APPROVED"
                        }`}
                      </td>
                    </tr>
                  </tbody>
                </table>
                {/* Payroll Breakdown Section */}
                <div className="mt-3">
                  <h6 className="fw-bold mb-2">PAYROLL BREAKDOWN</h6>
                  <AntdTable
                    columns={payrollBreakdownColumns}
                    data={payrollBreakdownData}
                    pagination={false}
                    showHeader={true}
                    bordered={true}
                    size="small"
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default PayrollDetails;
